import {
  FormEvent,
  FormEventHandler,
  HTMLProps,
  PropsWithChildren,
  Ref,
} from 'react';
import {
  FieldValues,
  FormProvider,
  FormProviderProps,
  SubmitErrorHandler,
  SubmitHandler,
} from 'react-hook-form';

export type FormProps<
  TInput extends FieldValues,
  TOutput extends FieldValues | undefined = undefined,
> = FormProviderProps<TInput, null, TOutput> & {
  onSubmit: SubmitHandler<TOutput extends undefined ? TInput : TOutput>;
  onSubmitError?: SubmitErrorHandler<
    TOutput extends undefined ? TInput : TOutput
  >;
  formProps?: Partial<HTMLProps<HTMLFormElement>>;
  className?: string;
  disableSubmitOnEnter?: boolean;
  ref?: Ref<HTMLFormElement>;
};

const withoutPropagation =
  (
    callback: (event: FormEvent<HTMLFormElement>) => void
  ): FormEventHandler<HTMLFormElement> =>
  event => {
    event.stopPropagation();
    callback(event);
  };

export const Form = <
  TData extends FieldValues = FieldValues,
  TOutput extends FieldValues | undefined = undefined,
>({
  onSubmit,
  disableSubmitOnEnter,
  onSubmitError,
  formProps,
  className,
  children,
  ref,
  ...props
}: PropsWithChildren<
  FormProps<TData, TOutput extends undefined ? TData : TOutput>
>) => (
  <FormProvider {...props}>
    <form
      ref={ref}
      noValidate
      role="form"
      {...formProps}
      onKeyDown={e => {
        if (disableSubmitOnEnter && e.key == 'Enter') e.preventDefault();
      }}
      onSubmit={withoutPropagation(
        // For some reason rhf or ts does not handle the type inferring here properly
        // eslint-disable-next-line
        (props.handleSubmit as any)(onSubmit, onSubmitError)
      )}
      className={className}
    >
      {children}
    </form>
  </FormProvider>
);
